import { useState, useCallback } from "react";

const CELL_TYPES = {
  empty:    { label: '빈칸',  color: '#1a1a2e', border: '#2d2d4e', icon: '·' },
  spot:     { label: '주차면', color: '#0d4f3c', border: '#10b981', icon: '🅿' },
  road:     { label: '도로',  color: '#1e2a1e', border: '#4b5563', icon: '↔' },
  pillar:   { label: '기둥',  color: '#2d1f0e', border: '#92400e', icon: '▪' },
  wall:     { label: '벽',    color: '#1f1f1f', border: '#374151', icon: '█' },
  entrance: { label: '입구',  color: '#1a1a3e', border: '#6366f1', icon: '🚗' },
};

const TYPE_ORDER = ['empty', 'spot', 'road', 'pillar', 'wall', 'entrance'];

function cycleType(current) {
  const idx = TYPE_ORDER.indexOf(current);
  return TYPE_ORDER[(idx + 1) % TYPE_ORDER.length];
}

function generateSpotNumber(grid, col, row) {
  const spotCount = grid.flat().filter(c => c.type === 'spot').length;
  return `P${String(spotCount + 1).padStart(2, '0')}`;
}

export default function ParkingLayoutDesigner() {
  const [cols, setCols] = useState(5);
  const [rows, setRows] = useState(6);
  const [tenantId, setTenantId] = useState('ADRIA_01');
  const [selectedType, setSelectedType] = useState('spot');
  const [grid, setGrid] = useState(() => initGrid(5, 6));
  const [editingCell, setEditingCell] = useState(null);
  const [editName, setEditName] = useState('');
  const [isDragging, setIsDragging] = useState(false);
  const [showOutput, setShowOutput] = useState(false);

  function initGrid(c, r) {
    return Array.from({ length: r }, (_, row) =>
      Array.from({ length: c }, (_, col) => ({
        type: 'empty', spot_no: null, col, row
      }))
    );
  }

  function applyGrid(newCols, newRows) {
    setGrid(prev => {
      return Array.from({ length: newRows }, (_, row) =>
        Array.from({ length: newCols }, (_, col) => {
          if (prev[row] && prev[row][col]) return { ...prev[row][col], col, row };
          return { type: 'empty', spot_no: null, col, row };
        })
      );
    });
  }

  const handleCellClick = useCallback((col, row) => {
    setGrid(prev => {
      const next = prev.map(r => r.map(c => ({ ...c })));
      const cell = next[row][col];
      const newType = selectedType;

      if (newType === 'spot' && cell.type !== 'spot') {
        cell.spot_no = generateSpotNumber(prev, col, row);
      } else if (newType !== 'spot') {
        cell.spot_no = null;
      }
      cell.type = newType;
      return next;
    });
  }, [selectedType]);

  const handleDoubleClick = (col, row) => {
    const cell = grid[row][col];
    if (cell.type === 'spot') {
      setEditingCell({ col, row });
      setEditName(cell.spot_no || '');
    }
  };

  const saveEditName = () => {
    if (!editingCell) return;
    const { col, row } = editingCell;
    setGrid(prev => {
      const next = prev.map(r => r.map(c => ({ ...c })));
      next[row][col].spot_no = editName;
      return next;
    });
    setEditingCell(null);
  };

  const generateCode = () => {
    const cells = grid.flat();
    const lines = cells.map(c =>
      `    { col: ${c.col}, row: ${c.row}, type: '${c.type}'${c.spot_no ? `, spot_no: '${c.spot_no}'` : ''} },`
    );
    return `// 주차 레이아웃 — ${tenantId}
// 생성일: ${new Date().toLocaleDateString('ko-KR')}
// 그리드: ${cols}열 × ${rows}행

const layout = {
  meta: {
    cols: ${cols},
    rows: ${rows},
    label: '${tenantId} 주차장',
  },
  cells: [
${lines.join('\n')}
  ],
};

export default layout;
`;
  };

  const downloadFile = () => {
    const code = generateCode();
    const blob = new Blob([code], { type: 'text/javascript' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `layout_${tenantId}.js`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const spotCount = grid.flat().filter(c => c.type === 'spot').length;

  return (
    <div style={{
      minHeight: '100vh',
      backgroundColor: '#0a0a0f',
      color: '#e2e8f0',
      fontFamily: "'JetBrains Mono', 'Courier New', monospace",
      padding: '24px',
    }}>

      {/* 헤더 */}
      <div style={{ maxWidth: '900px', margin: '0 auto' }}>
        <div style={{ marginBottom: '28px' }}>
          <div style={{ fontSize: '11px', color: '#4ade80', letterSpacing: '4px', marginBottom: '6px' }}>
            AMOS PARKING SYSTEM
          </div>
          <h1 style={{ fontSize: '28px', fontWeight: '900', color: '#f8fafc', margin: 0,
                       letterSpacing: '-1px' }}>
            레이아웃 디자이너
          </h1>
          <div style={{ fontSize: '12px', color: '#64748b', marginTop: '4px' }}>
            셀을 클릭해 배치 → 더블클릭으로 번호 수정 → JS 파일 다운로드
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '280px 1fr', gap: '20px' }}>

          {/* 왼쪽 패널 */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>

            {/* 테넌트 ID */}
            <div style={{ backgroundColor: '#111827', borderRadius: '12px', padding: '16px',
                          border: '1px solid #1f2937' }}>
              <div style={{ fontSize: '10px', color: '#6b7280', letterSpacing: '2px', marginBottom: '8px' }}>
                TENANT ID
              </div>
              <input value={tenantId} onChange={e => setTenantId(e.target.value)}
                style={{ width: '100%', backgroundColor: '#0a0a0f', border: '1px solid #374151',
                         borderRadius: '8px', padding: '8px 10px', color: '#4ade80',
                         fontSize: '13px', fontFamily: 'inherit', boxSizing: 'border-box', outline: 'none' }} />
            </div>

            {/* 그리드 크기 */}
            <div style={{ backgroundColor: '#111827', borderRadius: '12px', padding: '16px',
                          border: '1px solid #1f2937' }}>
              <div style={{ fontSize: '10px', color: '#6b7280', letterSpacing: '2px', marginBottom: '12px' }}>
                GRID SIZE
              </div>
              <div style={{ display: 'flex', gap: '10px', marginBottom: '10px' }}>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: '10px', color: '#9ca3af', marginBottom: '4px' }}>열 (COL)</div>
                  <input type="number" value={cols} min={1} max={12}
                    onChange={e => { const v = +e.target.value; setCols(v); applyGrid(v, rows); }}
                    style={{ width: '100%', backgroundColor: '#0a0a0f', border: '1px solid #374151',
                             borderRadius: '8px', padding: '8px', color: '#f8fafc',
                             fontSize: '16px', fontFamily: 'inherit', textAlign: 'center',
                             boxSizing: 'border-box', outline: 'none' }} />
                </div>
                <div style={{ display: 'flex', alignItems: 'flex-end', paddingBottom: '8px', color: '#374151', fontSize: '18px' }}>×</div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: '10px', color: '#9ca3af', marginBottom: '4px' }}>행 (ROW)</div>
                  <input type="number" value={rows} min={1} max={20}
                    onChange={e => { const v = +e.target.value; setRows(v); applyGrid(cols, v); }}
                    style={{ width: '100%', backgroundColor: '#0a0a0f', border: '1px solid #374151',
                             borderRadius: '8px', padding: '8px', color: '#f8fafc',
                             fontSize: '16px', fontFamily: 'inherit', textAlign: 'center',
                             boxSizing: 'border-box', outline: 'none' }} />
                </div>
              </div>
              <button onClick={() => { setGrid(initGrid(cols, rows)); }}
                style={{ width: '100%', padding: '8px', backgroundColor: '#1f2937',
                         border: '1px solid #374151', borderRadius: '8px', color: '#9ca3af',
                         fontSize: '11px', cursor: 'pointer', fontFamily: 'inherit' }}>
                🗑 그리드 초기화
              </button>
            </div>

            {/* 셀 타입 선택 */}
            <div style={{ backgroundColor: '#111827', borderRadius: '12px', padding: '16px',
                          border: '1px solid #1f2937' }}>
              <div style={{ fontSize: '10px', color: '#6b7280', letterSpacing: '2px', marginBottom: '12px' }}>
                CELL TYPE
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                {Object.entries(CELL_TYPES).map(([type, cfg]) => (
                  <button key={type} onClick={() => setSelectedType(type)}
                    style={{ padding: '10px 12px', borderRadius: '8px', cursor: 'pointer',
                             fontFamily: 'inherit', fontSize: '12px', textAlign: 'left',
                             display: 'flex', alignItems: 'center', gap: '10px',
                             backgroundColor: selectedType === type ? cfg.color : 'transparent',
                             border: `2px solid ${selectedType === type ? cfg.border : '#1f2937'}`,
                             color: selectedType === type ? '#f8fafc' : '#6b7280',
                             transition: 'all 0.15s' }}>
                    <span style={{ fontSize: '16px' }}>{cfg.icon}</span>
                    <span>{cfg.label}</span>
                    {selectedType === type && (
                      <span style={{ marginLeft: 'auto', fontSize: '10px', color: cfg.border }}>선택됨</span>
                    )}
                  </button>
                ))}
              </div>
            </div>

            {/* 통계 */}
            <div style={{ backgroundColor: '#111827', borderRadius: '12px', padding: '16px',
                          border: '1px solid #1f2937' }}>
              <div style={{ fontSize: '10px', color: '#6b7280', letterSpacing: '2px', marginBottom: '12px' }}>
                STATS
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px' }}>
                <span style={{ fontSize: '12px', color: '#9ca3af' }}>총 주차면</span>
                <span style={{ fontSize: '16px', fontWeight: '700', color: '#4ade80' }}>{spotCount}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span style={{ fontSize: '12px', color: '#9ca3af' }}>그리드</span>
                <span style={{ fontSize: '12px', color: '#94a3b8' }}>{cols} × {rows}</span>
              </div>
            </div>

            {/* 출력 버튼 */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              <button onClick={() => setShowOutput(!showOutput)}
                style={{ padding: '12px', backgroundColor: '#1e3a5f', border: '1px solid #2d5a8e',
                         borderRadius: '10px', color: '#93c5fd', fontSize: '12px',
                         cursor: 'pointer', fontFamily: 'inherit', fontWeight: '700' }}>
                {showOutput ? '▲ 코드 숨기기' : '▼ 코드 보기'}
              </button>
              <button onClick={downloadFile}
                style={{ padding: '12px', backgroundColor: '#064e3b', border: '1px solid #059669',
                         borderRadius: '10px', color: '#4ade80', fontSize: '12px',
                         cursor: 'pointer', fontFamily: 'inherit', fontWeight: '700' }}>
                ⬇ layout_{tenantId}.js 다운로드
              </button>
            </div>
          </div>

          {/* 오른쪽 — 그리드 에디터 */}
          <div>
            <div style={{ backgroundColor: '#111827', borderRadius: '12px', padding: '20px',
                          border: '1px solid #1f2937' }}>
              <div style={{ fontSize: '10px', color: '#6b7280', letterSpacing: '2px', marginBottom: '16px' }}>
                LAYOUT EDITOR — 클릭: 타입 변경 / 더블클릭: 번호 수정
              </div>

              {/* 그리드 — 고정 크기, 핸드폰 미리보기 */}
              <div style={{
                display: 'grid',
                gridTemplateColumns: `repeat(${cols}, 1fr)`,
                gridTemplateRows: `repeat(${rows}, 1fr)`,
                gap: '3px',
                width: '100%',
                height: '360px',
                userSelect: 'none',
              }}>
                {grid.map((row, ri) =>
                  row.map((cell, ci) => {
                    const cfg = CELL_TYPES[cell.type];
                    const isEditing = editingCell?.col === ci && editingCell?.row === ri;
                    return (
                      <div key={`${ci}-${ri}`}
                        onClick={() => handleCellClick(ci, ri)}
                        onDoubleClick={() => handleDoubleClick(ci, ri)}
                        style={{
                          backgroundColor: cfg.color,
                          border: `2px solid ${cfg.border}`,
                          borderRadius: cell.type === 'pillar' ? '3px' : '6px',
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          justifyContent: 'center',
                          cursor: 'pointer',
                          transition: 'all 0.1s',
                          position: 'relative',
                          padding: cell.type === 'pillar' ? '2px' : '0',
                        }}>
                        {isEditing ? (
                          <input autoFocus value={editName}
                            onChange={e => setEditName(e.target.value)}
                            onBlur={saveEditName}
                            onKeyDown={e => e.key === 'Enter' && saveEditName()}
                            onClick={e => e.stopPropagation()}
                            style={{ width: '90%', backgroundColor: 'transparent', border: 'none',
                                     color: '#4ade80', fontSize: '10px', textAlign: 'center',
                                     fontFamily: 'inherit', outline: 'none' }} />
                        ) : (
                          <>
                            {cell.type === 'spot' && (
                              <div style={{ fontSize: '9px', color: '#4ade80', fontWeight: '700',
                                            textAlign: 'center', lineHeight: '1.2' }}>
                                {cell.spot_no || '?'}
                              </div>
                            )}
                            {cell.type !== 'spot' && cell.type !== 'empty' && (
                              <div style={{ fontSize: '14px', opacity: 0.5 }}>{cfg.icon}</div>
                            )}
                            <div style={{ fontSize: '8px', color: cfg.border, opacity: 0.6,
                                          position: 'absolute', bottom: '2px', right: '3px' }}>
                              {ci},{ri}
                            </div>
                          </>
                        )}
                      </div>
                    );
                  })
                )}
              </div>
            </div>

            {/* 코드 출력 */}
            {showOutput && (
              <div style={{ backgroundColor: '#0d1117', borderRadius: '12px', padding: '16px',
                            border: '1px solid #1f2937', marginTop: '12px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                  <div style={{ fontSize: '10px', color: '#6b7280', letterSpacing: '2px' }}>OUTPUT</div>
                  <button onClick={() => navigator.clipboard.writeText(generateCode())}
                    style={{ padding: '4px 10px', backgroundColor: '#1f2937', border: '1px solid #374151',
                             borderRadius: '6px', color: '#9ca3af', fontSize: '10px',
                             cursor: 'pointer', fontFamily: 'inherit' }}>
                    📋 복사
                  </button>
                </div>
                <pre style={{ margin: 0, fontSize: '10px', color: '#4ade80', overflowX: 'auto',
                              lineHeight: '1.6', maxHeight: '300px', overflowY: 'auto' }}>
                  {generateCode()}
                </pre>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
